<script setup>
/* Fester Kopf der Startansicht: Logo-Umlaufbahn, Titel/Untertitel der aktiven Seite, Seitenanzeige.
   Ersetzt den früheren PageHead (Reiter, Pfeile ab 1024 px). Der Kopf wischt nicht mehr mit — nur die Bahn dreht
   sich; die Listen darunter schieben sich (SquadPager.vue).

   Alles, was sich bewegt, kommt aus der gleitenden Seitenposition `pos` (Kommazahl, 0 = erste Seite) über
   die reinen Funktionen in domain/squadPages.js (orbitPlacement, wrapOffset, titleOpacity). Hier steht
   keine Mathematik, nur Darstellung.

   Bedienung: Pfeil-Knöpfe links/rechts der Bühne (bei jeder Breite), Segmente (role=tablist), Tippen auf ein
   hinteres Logo (eine Stufe in dessen Richtung), Pfeiltasten. Wischen steuert der Pager.
   Fokusreihenfolge: Pfeil links, Logos, Pfeil rechts, Segmente.

   Gestaltung (Startseite neu, 2026-10): Das vordere Logo trägt Ring und Schein in der Akzentfarbe; beides blendet
   über `front` (Nähe zur Vorderseite) ein und aus und wandert so beim Drehen mit. Seitliche Logos tragen eine
   dauerhafte Beschriftung (tabKey), die dem Logo folgt. Alles Visuelle; die Platzierung bleibt in
   domain/squadPages.js unverändert.

   Club-Auswahl: gehört zum Kader. Gibt es mehrere Clubs in domain/clubs.js, stehen kleine Wappen-Knöpfe
   unter dem Untertitel, solange der Kader die aktive Seite ist. Heute gibt es genau einen Club: dann
   erscheint nichts (der frühere, nicht anklickbare Kreis ist ersetzt durch das Logo auf der Bahn).

   Vor der Anmeldung: mit nur einer Seite (App.vue) — ein Logo vorn, weder Pfeile noch Punkte noch Bahn. */
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { mdiChevronLeft, mdiChevronRight } from '@mdi/js';
import { CLUBS } from '../domain/clubs.js';
import {
  ORBIT, orbitPlacement, wrapOffset, titleOpacity, canStep, deltaBetween,
} from '../domain/squadPages.js';

const stage = ref(null);
const props = defineProps({
  pages: { type: Array, required: true },
  /* gleitende Position (Kommazahl) */
  pos: { type: Number, required: true },
  /* Seite, auf der die Bahn einrastet (Punkte, Pfeile, Titel für Vorleser) */
  activeIndex: { type: Number, required: true },
});
const emit = defineEmits(['step', 'select']);

/* Die Bühne (Logo-Umlaufbahn) gibt der Pager nach außen: Er prüft, ob sie aus dem Bild gescrollt ist. */
defineExpose({ stage });

const n = computed(() => props.pages.length);
const multi = computed(() => n.value > 1);

/* Breite der Bühne: gemessen; ohne Browser (Tests) eine Handy-Breite */
const stageWidth = ref(280);
let observer = null;
onMounted(() => {
  if (!stage.value) return;
  stageWidth.value = stage.value.clientWidth || stageWidth.value;
  if (typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(() => {
      stageWidth.value = stage.value?.clientWidth || stageWidth.value;
    });
    observer.observe(stage.value);
  }
});
onBeforeUnmount(() => observer?.disconnect());

const activeClubId = ref(CLUBS[0].id);
const activeClub = computed(() => CLUBS.find((c) => c.id === activeClubId.value) || CLUBS[0]);

const items = computed(() =>
  props.pages.map((page, i) => {
    const d = wrapOffset(i - props.pos, n.value);
    const place = orbitPlacement(d, { n: n.value, stageWidth: stageWidth.value });
    const isClub = !page.logo;
    const box = ORBIT.logoSize;
    const ring = Math.round(Math.min(1, Math.max(0, (place.front - 0.55) / 0.45)) * 1000) / 1000;
    return {
      page,
      i,
      d,
      place,
      src: `/${isClub ? activeClub.value.crest : page.logo}`,
      isClub,
      imgSize: Math.round(box * (page.logoScale ?? 1)),
      label: isClub ? activeClub.value.name : null,
      style: {
        left: `${place.x - box / 2}px`,
        top: `${place.y - box / 2}px`,
        width: `${box}px`,
        height: `${box}px`,
        opacity: place.opacity,
        zIndex: place.zIndex,
        transform: `scale(${place.scale})`,
        filter: place.dim ? `brightness(${ORBIT.dimBrightness})` : undefined,
      },
      titleStyle: { opacity: titleOpacity(d) },
      /* Ring und Schein: erst ab etwa halber Nähe zur Vorderseite, ganz sichtbar vorn */
      ringStyle: { opacity: ring },
      /* Beschriftung unter dem Logo: folgt dem Logo (Mitte, Skalierung) und blendet mit dessen Deckkraft;
         die Schrift selbst wird nicht skaliert. Etwas kräftiger als das Logo, damit sie lesbar bleibt;
         vorn (Ring sichtbar) blendet sie aus, dort steht der Titel. */
      captionStyle: {
        left: `${place.x}px`,
        top: `${place.y + (box * place.scale) / 2 + 4}px`,
        opacity: (0.5 + 0.5 * place.opacity) * (1 - ring),
        zIndex: place.zIndex,
      },
    };
  })
);

/* Gestrichelte Bahn hinter den Logos: die Ellipse, auf der die Logo-Mitten laufen */
const orbitEllipse = computed(() => ({
  cx: stageWidth.value / 2,
  cy: ORBIT.stageHeight * ORBIT.cyRatio - ORBIT.lift,
  rx: ORBIT.rxRatio * stageWidth.value,
  ry: ORBIT.ry * ORBIT.depthFlatten,
}));

const subtitleOf = (it, t) => t('app.tag');

const showClubSwitch = computed(() => CLUBS.length > 1 && !props.pages[props.activeIndex]?.logo);
function selectClub(id) {
  activeClubId.value = id;
}

/* Tippen auf ein hinteres Logo: eine Stufe in dessen Richtung (Vorzeichen von d) */
function onLogo(it) {
  emit('step', it.d < 0 ? -1 : 1);
}

/* Pfeiltasten schalten, auch wenn der Fokus auf einem Punkt oder Logo liegt; liegt er auf einem Punkt,
   wandert er mit zum neuen aktiven Punkt. */
const root = ref(null);
function onKey(e) {
  if (!multi.value) return;
  const delta = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
  if (!delta) return;
  e.preventDefault();
  if (!canStep(props.activeIndex, delta, n.value)) return;
  emit('step', delta);
  if (e.target?.getAttribute?.('role') === 'tab') {
    const next = (props.activeIndex + delta + n.value) % n.value;
    queueMicrotask(() => root.value?.querySelector(`#squad-tab-${props.pages[next].id}`)?.focus());
  }
}

const onDot = (i) => emit('select', deltaBetween(props.activeIndex, i, n.value));
</script>

<template>
  <div ref="root" class="page-head orbit-head" @keydown="onKey">
    <div class="orbit-row">
      <button
        v-if="multi"
        type="button"
        class="head-arrow"
        :disabled="!canStep(activeIndex, -1, n)"
        :aria-label="$t('squadPages.previous')"
        @click="emit('step', -1)"
      >
        <v-icon :icon="mdiChevronLeft" size="26" />
      </button>

      <div ref="stage" class="orbit-stage">
        <svg v-if="multi" class="orbit-line" aria-hidden="true" focusable="false">
          <ellipse
            :cx="orbitEllipse.cx"
            :cy="orbitEllipse.cy"
            :rx="orbitEllipse.rx"
            :ry="orbitEllipse.ry"
            fill="none"
            stroke="currentColor"
            stroke-width="1"
            stroke-dasharray="3 7"
          />
        </svg>

        <template v-for="it in items" :key="it.page.id">
          <!-- Das aktive Logo ist keine Schaltfläche, die hinteren schon (eine Stufe in deren Richtung) -->
          <button
            v-if="multi && it.i !== activeIndex"
            type="button"
            class="orbit-logo orbit-logo-btn"
            :style="it.style"
            :aria-label="$t(it.page.tabKey)"
            @click="onLogo(it)"
          >
            <span class="orbit-ring" :style="it.ringStyle" aria-hidden="true"></span>
            <img :src="it.src" alt="" :width="it.imgSize" :height="it.imgSize" :class="{ 'orbit-crest': it.isClub }" />
          </button>
          <div v-else class="orbit-logo" :style="it.style">
            <span class="orbit-ring" :style="it.ringStyle" aria-hidden="true"></span>
            <img :src="it.src" :alt="it.label || ''" :width="it.imgSize" :height="it.imgSize" :class="{ 'orbit-crest': it.isClub }" />
          </div>
        </template>

        <!-- Dauerhafte Beschriftung unter den Logos (der Knopf trägt schon den Namen für Vorleser) -->
        <template v-if="multi">
          <span
            v-for="it in items"
            :key="`caption-${it.page.id}`"
            class="orbit-caption"
            :style="it.captionStyle"
            aria-hidden="true"
          >{{ $t(it.page.tabKey) }}</span>
        </template>
      </div>

      <button
        v-if="multi"
        type="button"
        class="head-arrow"
        :disabled="!canStep(activeIndex, 1, n)"
        :aria-label="$t('squadPages.next')"
        @click="emit('step', 1)"
      >
        <v-icon :icon="mdiChevronRight" size="26" />
      </button>
    </div>

    <!-- Titel blenden beim Drehen ineinander; für Vorleser steht nur der Titel der aktiven Seite da -->
    <div class="orbit-titles" aria-live="polite">
      <span class="sr-only">
        {{ items[activeIndex] ? (items[activeIndex].isClub ? activeClub.name : $t(items[activeIndex].page.titleKey)) : '' }}
      </span>
      <div v-for="it in items" :key="it.page.id" class="orbit-title" :style="it.titleStyle" aria-hidden="true">
        <div class="club-name">{{ it.isClub ? activeClub.name : $t(it.page.titleKey) }}</div>
        <div class="tag">{{ subtitleOf(it, $t) }}</div>
      </div>
    </div>

    <div v-if="showClubSwitch" class="club-switch">
      <button
        v-for="club in CLUBS"
        :key="club.id"
        type="button"
        class="club-dot"
        :class="{ active: club.id === activeClub.id }"
        :aria-pressed="club.id === activeClub.id"
        :aria-label="club.name"
        @click="selectClub(club.id)"
      >
        <img :src="`/${club.crest}`" alt="" />
      </button>
    </div>

    <div v-if="multi" class="orbit-dots" role="tablist" :aria-label="$t('squadPages.label')">
      <button
        v-for="(p, i) in pages"
        :id="`squad-tab-${p.id}`"
        :key="p.id"
        type="button"
        role="tab"
        class="orbit-dot"
        :class="{ active: i === activeIndex }"
        :aria-selected="i === activeIndex"
        :aria-controls="`squad-page-${p.id}`"
        :aria-label="$t(p.tabKey)"
        :tabindex="i === activeIndex ? 0 : -1"
        @click="onDot(i)"
      >
        <span class="orbit-dot-mark" aria-hidden="true"></span>
      </button>
    </div>

  </div>
</template>

<style scoped>
.page-head {
  /* Unten knapp: nach den Segmenten (44 px Klickfläche, 3 px Strich in der Mitte) folgt gleich die Trennlinie */
  padding: 0 12px 0;
  text-align: center;
  position: relative;
}
.orbit-row {
  display: flex;
  align-items: center;
  gap: 4px;
  /* F3: Über dem vorderen Logo liegen in der 230 px hohen Bühne 92 px leerer Raum (Logo-Kasten 128 px, Mitte bei 62 %).
     Die ganze Bühne rückt 68 px nach oben, Logo, Ring, Bahn und Pfeile wandern gemeinsam mit; es bleiben 24 px zwischen
     "HUB" und dem Ring. Nur ein Abstand — die Platzierung in domain/squadPages.js ist unverändert. */
  margin-top: -68px;
}
.orbit-stage {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  height: 230px;
  isolation: isolate;
}
.orbit-line {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  color: var(--green);
  opacity: 0.22;
  pointer-events: none;
}
.orbit-logo {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: transparent;
  border: none;
  transform-origin: center;
  will-change: transform;
}
.orbit-logo img {
  display: block;
  object-fit: contain;
}
/* Ring (1,5 px) und weicher Schein am Logo-Kasten; die Sichtbarkeit kommt inline aus front. Akzentfarbe ist
   --green (Sky im Dunkeln, die dunklere Variante im Hellen); der Schein hat 25 % davon. */
.orbit-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 1.5px solid var(--green);
  box-shadow: 0 0 60px color-mix(in srgb, var(--green) 25%, transparent);
  pointer-events: none;
}
.orbit-caption {
  position: absolute;
  transform: translateX(-50%);
  white-space: nowrap;
  pointer-events: none;
  font-family: var(--display);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  /* --muted statt --dim: 11 px braucht 4,5:1, --dim reicht im hellen Modus nicht (4,1:1) */
  color: var(--muted);
}
.orbit-logo img.orbit-crest {
  border-radius: 50%;
}
.orbit-logo-btn {
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.orbit-logo-btn:focus-visible {
  outline: 2px solid var(--green);
  outline-offset: -6px;
  border-radius: 50%;
}
.head-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex: none;
  padding: 0;
  border-radius: 50%;
  background: transparent;
  border: 1px solid var(--border-soft);
  color: var(--muted);
  cursor: pointer;
  transition: color 0.14s, border-color 0.14s;
  position: relative;
  z-index: 3;
}
.head-arrow:hover:not(:disabled) {
  color: var(--green);
  border-color: var(--green-dim);
}
.head-arrow:focus-visible {
  outline: 2px solid var(--green);
  outline-offset: 2px;
}
.head-arrow:disabled {
  opacity: 0.3;
  cursor: default;
}
.orbit-titles {
  display: grid;
  margin-top: 4px;
}
.orbit-title {
  grid-area: 1 / 1;
}
/* Titelzeile 22 px / 600 / 0,12em, Untertitel 13 px (die Grundregeln stehen in main.css) */
.orbit-title .club-name {
  font-size: 22px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}
.orbit-title .tag {
  margin-top: 8px;
  font-size: 13px;
  color: var(--muted);
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}
.club-switch {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 10px;
}
.club-dot {
  width: 30px;
  height: 30px;
  padding: 0;
  border-radius: 50%;
  background: var(--card);
  border: 1px solid var(--border);
  cursor: pointer;
}
.club-dot.active {
  border-color: var(--green);
}
.club-dot img {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  object-fit: cover;
}
/* Seitenanzeige als Segmente: zusammen etwa 120 px breit, optisch 3 px hoch, die Klickfläche bleibt 44 px hoch */
.orbit-dots {
  display: flex;
  justify-content: center;
  gap: 4px;
  width: 120px;
  max-width: 100%;
  /* F3: Die Klickfläche (44 px) ragt 20 px unter den Strich; unten 17 px zurücknehmen, damit zwischen Strich und
     Trennlinie etwa 16 px bleiben (Handy und Laptop gleich) */
  margin: 4px auto -17px;
}
.orbit-dot {
  flex: 1 1 0;
  min-width: 0;
  height: 44px;
  display: flex;
  align-items: center;
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;
}
.orbit-dot-mark {
  display: block;
  width: 100%;
  height: 3px;
  border-radius: 2px;
  background: var(--border);
  transition: background 0.14s;
}
.orbit-dot:hover .orbit-dot-mark {
  background: var(--dim);
}
.orbit-dot.active .orbit-dot-mark {
  background: var(--text);
}
.orbit-dot:focus-visible {
  outline: 2px solid var(--green);
  outline-offset: 2px;
  border-radius: 4px;
}
@media (prefers-reduced-motion: reduce) {
  .orbit-dot-mark,
  .head-arrow {
    transition: none;
  }
}
</style>
